import React, { useEffect, useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabase'; 
import '../styles/Requests.css'; 
import FileUpload from '../components/FileUpload'; 
import UrlUpload from '../components/UrlUpload'; 
import TextUpload from '../components/TextUpload';  
import DocumentResubmit from '../components/DocumentResubmit';
import ReactMarkdown from 'react-markdown';
import { MinusIcon, PlusIcon, CheckIcon, RotateCcw } from 'lucide-react';

const Accordion = ({ id, title, children, activeAccordion, setActiveAccordion }) => {
  const isOpen = activeAccordion === id;
  
  const toggleAccordion = () => {
    setActiveAccordion(isOpen ? null : id);
  };

  return (
    <div className="accordion">
      <div className="accordion-header" onClick={toggleAccordion}>
        <h3 className="accordion-title">{title}</h3>
        <span className="accordion-icon">{isOpen ? <MinusIcon size={16} /> : <PlusIcon size={16} />}</span>
      </div>
      {isOpen && (
        <div className="accordion-content">
          {children}
        </div>
      )}
    </div>
  );
};

function Requests() {
  const { profileUserId, selectedWorkspaceId } = useWorkspace();
  const { language } = useLanguage();
  const [userName, setUserName] = useState('');
  const [openRequests, setOpenRequests] = useState(0);
  const [tasks, setTasks] = useState([]);
  const [statusFilter, setStatusFilter] = useState('open'); 
  const [selectedTask, setSelectedTask] = useState(null);  
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [expandedTaskId, setExpandedTaskId] = useState(null);

  const formatDate = (date) => {
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return new Date(date).toLocaleDateString(language === 'en' ? 'en-GB' : 'de-DE', options);
  };

  const shouldShowDeadline = (date) => {
    if (!date) return false;
    const deadlineDate = new Date(date);
    return deadlineDate.getTime() !== new Date('1970-01-01').getTime();
  };

  const displayStatus = (status) => {
    if (status === 'open/open' || status === 'in_progress/reopened') {
      return language === 'en' ? 'Open' : 'Offen';
    } else if (status === 'in_progress/submitted') {
      return language === 'en' ? 'Submitted' : 'Eingereicht';
    } else if (status === 'done/done') {
      return language === 'en' ? 'Done' : 'Erledigt';
    } else if (status === 'in_progress/in_progress') {
      return language === 'en' ? 'In Progress' : 'In Bearbeitung';
    }
    return status;
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'open/open' || status === 'in_progress/reopened') {
      return 'status-open';
    } else if (status === 'in_progress/submitted') {
      return 'status-submitted';
    } else if (status === 'done/done') {
      return 'status-done';
    } else if (status === 'in_progress/in_progress') {
      return 'status-in-progress';
    }
    return '';
  };

  useEffect(() => {
    const fetchUserName = async () => {
      if (!profileUserId) return;
      const { data, error } = await supabase
        .from('user_profiles')
        .select('firstname')
        .eq('id', profileUserId)
        .maybeSingle();
      if (error) {
        console.error('Error fetching user first name:', error.message);
      } else if (data) {
        setUserName(data.firstname || (language === 'en' ? 'User' : 'Benutzer'));
      }
    };

    fetchUserName();
  }, [profileUserId, language]);

  useEffect(() => {
    if (!selectedWorkspaceId) return;

    const fetchOpenRequestsData = async () => {
      const { data, error } = await supabase
        .from('tasks') 
        .select('id, status, due_date, name, description, expected_doc, action_link') 
        .eq('workspace_id', selectedWorkspaceId)
        .neq('is_done', true)
        .in('status', ['open/open', 'in_progress/reopened'])
        .order('due_date', { ascending: false });
  
      if (error) {
        console.error('Error fetching open requests:', error.message);
        setOpenRequests(0);
        setTasks([]);
      } else {
        setOpenRequests(data?.length || 0);
        if (statusFilter === 'open') {
          setTasks(data || []);
        }
      }
    };

    const fetchTasksByStatus = async (statuses) => {
      const { data, error } = await supabase
        .from('tasks')
        .select('id, name, description, due_date, expected_doc, action_link, status')  
        .eq('workspace_id', selectedWorkspaceId)
        .in('status', statuses);  
  
      if (error) {
        console.error('Error fetching tasks:', error.message);
        setTasks([]);
      } else {
        setTasks(data || []);
      }
    };

    if (statusFilter === 'open') {
      fetchOpenRequestsData();
    } else if (statusFilter === 'pending') {
      fetchTasksByStatus(['in_progress/submitted']);
    } else if (statusFilter === 'done') {
      fetchTasksByStatus(['done/done']);
    }
  }, [selectedWorkspaceId, statusFilter]);

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setSelectedTask(null);
  };

  const handleTaskClick = (task) => {
    if (statusFilter !== 'open') return;  
    setSelectedTask(task);  
  };

  const handleSubmitTask = async (event, taskId) => {
    event.stopPropagation();

    try {
      const { data, error } = await supabase
        .from('tasks')
        .update({ status: 'in_progress/submitted' })
        .eq('id', taskId);

      if (error) {
        console.error('Error updating task status:', error.message);
      } else {
        console.log('Task status updated:', data);
        setSelectedTask(null);
        
        // Refetch tasks based on current filter
        if (statusFilter === 'open') {
          const { data: openData, error: openError } = await supabase
            .from('tasks')
            .select('id, status, due_date, name, description, expected_doc, action_link')
            .eq('workspace_id', selectedWorkspaceId)
            .neq('is_done', true)
            .in('status', ['open/open', 'in_progress/reopened'])
            .order('due_date', { ascending: false });
          
          if (!openError) {
            setTasks(openData || []);
          }
        } else if (statusFilter === 'pending') {
          const { data: pendingData, error: pendingError } = await supabase
            .from('tasks')
            .select('id, name, description, due_date, expected_doc, action_link, status')
            .eq('workspace_id', selectedWorkspaceId)
            .in('status', ['in_progress/submitted']);
          
          if (!pendingError) {
            setTasks(pendingData || []);
          }
        }
      }
    } catch (err) {
      console.error('Unexpected error:', err);
    }
  };

  const handleReopenTask = async (event, taskId) => {
    event.stopPropagation();

    try {
      const { data, error } = await supabase
        .from('tasks')
        .update({ status: 'in_progress/reopened' })
        .eq('id', taskId);

      if (error) {
        console.error('Error updating task status:', error.message);
      } else {
        console.log('Task status updated:', data);
        setSelectedTask(null);
        
        // Refetch tasks based on current filter
        if (statusFilter === 'open') {
          const { data: openData, error: openError } = await supabase
            .from('tasks')
            .select('id, status, due_date, name, description, expected_doc, action_link')
            .eq('workspace_id', selectedWorkspaceId)
            .neq('is_done', true)
            .in('status', ['open/open', 'in_progress/reopened'])
            .order('due_date', { ascending: false });
          
          if (!openError) {
            setTasks(openData || []);
          }
        } else if (statusFilter === 'pending') {
          const { data: pendingData, error: pendingError } = await supabase
            .from('tasks')
            .select('id, name, description, due_date, expected_doc, action_link, status')
            .eq('workspace_id', selectedWorkspaceId)
            .in('status', ['in_progress/submitted']);
          
          if (!pendingError) {
            setTasks(pendingData || []);
          }
        }
      }
    } catch (err) {
      console.error('Unexpected error:', err);
    }
  };

  useEffect(() => {
    setActiveAccordion(null);
  }, [selectedTask]);

  const toggleTask = (taskId) => {
    setExpandedTaskId(prevId => prevId === taskId ? null : taskId);
    if (expandedTaskId !== taskId) {
      const task = tasks.find(t => t.id === taskId);
      setSelectedTask(task);
    } else {
      setSelectedTask(null);
    }
  };

  const renderUploadComponent = () => {
    if (!selectedTask) return null;

    switch (selectedTask.expected_doc) {
      case 'text':
        return <TextUpload title={selectedTask.name} />; 
      case 'action':
        return (
          <a 
            href={selectedTask.action_link} 
            target="_blank" 
            rel="noopener noreferrer"
            className="action-button"
          >
            {language === 'en' ? 'Click here to take action' : 'Klicken Sie hier, um zu handeln'}
          </a>
        );        
      case 'doc': 
        return (
          <>
            <Accordion
              id="file"
              title={language === 'en' ? 'Upload File(s)' : 'Datei hochladen'}
              activeAccordion={activeAccordion}
              setActiveAccordion={setActiveAccordion}
            >
              <FileUpload task_id={selectedTask.id} />
            </Accordion>
            <Accordion
              id="url"
              title={language === 'en' ? 'Share URL(s)' : 'URL hochladen'}
              activeAccordion={activeAccordion}
              setActiveAccordion={setActiveAccordion}
            >
              <UrlUpload task_id={selectedTask.id} />
            </Accordion>
            <Accordion
              id="resubmit"
              title={language === 'en' ? 'Assign Previously Uploaded Document(s)' : 'Dokument erneut einreichen'}
              activeAccordion={activeAccordion}
              setActiveAccordion={setActiveAccordion}
            >
              <DocumentResubmit title={selectedTask.name} />
            </Accordion>
          </>
        );
      default:
        return <p>{language === 'en' ? 'No document type specified.' : 'Kein Dokumententyp angegeben.'}</p>;
    }
  };

  return (
    <div className="requests-container">
      <h1 className="requests-header">👋 {language === 'en' ? 'Hello' : 'Hallo'} {userName}!</h1>
      <p className="requests-description">
        {language === 'en' 
          ? <>There are <strong>{openRequests} open requests</strong> waiting for you.</>
          : <>Es warten <strong>{openRequests} offene Anfragen</strong> auf Sie.</>
        }
      </p>
      <p className="requests-description">
        {language === 'en' 
          ? 'Remember, please do not create any documents or inputs specifically for us.'
          : 'Bitte erstellen Sie keine Dokumente oder Eingaben speziell für uns.'
        }
      </p>

      <hr className="requests-divider" />

      <div className="tabs-container">
        <div 
          className={`tab-item ${statusFilter === 'open' ? 'active' : ''}`}
          onClick={() => handleStatusFilterChange('open')}
        >
          {language === 'en' ? 'Open' : 'Offen'}
        </div>
        <div 
          className={`tab-item ${statusFilter === 'pending' ? 'active' : ''}`}
          onClick={() => handleStatusFilterChange('pending')}
        >
          {language === 'en' ? 'Pending' : 'Ausstehend'}
        </div>
        <div 
          className={`tab-item ${statusFilter === 'done' ? 'active' : ''}`}
          onClick={() => handleStatusFilterChange('done')}
        >
          {language === 'en' ? 'Done' : 'Erledigt'}
        </div>
      </div>


      <div className="tasks-list">
        {statusFilter === 'open' && (
          <div>
            {tasks.length === 0 ? (
              <p className="empty-state-message">
                {language === 'en' 
                  ? 'No requests assigned to your workspace yet.'
                  : 'Noch keine Anfragen für Ihren Arbeitsbereich zugewiesen.'
                }
              </p>
            ) : (
              tasks.map((task, index) => (
                <div 
                  key={index} 
                  className="task-card"
                  style={{ position: 'relative' }}
                >
                  <div className="task-summary">
                    <div className="task-header">
                      <div 
                        className="task-title"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleTask(task.id);
                        }}
                      >
                        <span className="business-stats-title">{task.name}</span>
                      </div>
                      <button 
                        className="expand-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleTask(task.id);
                        }}
                      >
                        {expandedTaskId === task.id ? <MinusIcon size={16} /> : <PlusIcon size={16} />}
                      </button>
                    </div>
                    <hr className="white-line" />
                    <div style={{ position: 'relative' }}>
                      {shouldShowDeadline(task.due_date) && (
                        <div className="deadline-line">
                          <span>{language === 'en' ? 'Deadline:' : 'Frist:'}</span> {formatDate(task.due_date)}
                        </div>
                      )}
                      <div className="status-line">
                        <span className="stats-status-text">{language === 'en' ? 'Status:' : 'Status:'}</span>{" "}
                        <span className={`status-badge ${getStatusBadgeClass(task.status)}`}>
                          {displayStatus(task.status)}
                        </span>
                      </div>
                      <button
                        className="submit-task-button"
                        onClick={(e) => handleSubmitTask(e, task.id)}
                      >
                        <CheckIcon size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> {language === 'en' ? 'Mark as Submitted' : 'Als eingereicht markieren'}
                      </button>
                    </div>
                  </div>
                  {expandedTaskId === task.id && (
                    <div className="task-details">
                      <hr className="white-line" />
                      <ReactMarkdown>{task.description}</ReactMarkdown>
                      {renderUploadComponent()}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {statusFilter === 'pending' && (
          <div>
            {tasks.length === 0 ? (
              <p className="empty-state-message">
                {language === 'en' 
                  ? 'You don\'t have any submitted documents.'
                  : 'Sie haben keine eingereichten Dokumente.'
                }
              </p>
            ) : (
              tasks.map((task, index) => (
                <div 
                  key={index} 
                  className="task-card"
                  style={{ position: 'relative' }}
                >
                  <div className="task-summary">
                    <div className="task-header">
                      <div className="task-title">
                        <span className="business-stats-title">{task.name}</span>
                      </div>
                    </div>
                    <hr className="white-line" />
                    <div style={{ position: 'relative' }}>
                      {shouldShowDeadline(task.due_date) && (
                        <div className="deadline-line">
                          <span>{language === 'en' ? 'Deadline:' : 'Frist:'}</span> {formatDate(task.due_date)}
                        </div>
                      )}
                      <div className="status-line">
                        <span className="stats-status-text">{language === 'en' ? 'Status:' : 'Status:'}</span>{" "}
                        <span className={`status-badge ${getStatusBadgeClass(task.status)}`}>
                          {displayStatus(task.status)}
                        </span>
                      </div>
                      <button
                        className="reopen-task-button"
                        onClick={(e) => handleReopenTask(e, task.id)}
                      >
                        <RotateCcw size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> {language === 'en' ? 'Reopen' : 'Wiedereröffnen'}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {statusFilter === 'done' && (
          <div>
            {tasks.length === 0 ? (
              <p className="empty-state-message">
                {language === 'en' 
                  ? 'You don\'t have any completed documents.'
                  : 'Sie haben keine abgeschlossenen Dokumente.'
                }
              </p>
            ) : (
              tasks.map((task, index) => (
                <div 
                  key={index} 
                  className="task-card"
                >
                  <div className="task-summary">
                    <div className="task-header">
                      <div className="task-title">
                        <span className="business-stats-title">{task.name}</span>
                      </div>
                    </div>
                    <hr className="white-line" />
                    {shouldShowDeadline(task.due_date) && (
                      <div className="deadline-line">
                        <span>{language === 'en' ? 'Deadline:' : 'Frist:'}</span> {formatDate(task.due_date)}
                      </div>
                    )}
                    <div className="status-line">
                      <span className="stats-status-text">{language === 'en' ? 'Status:' : 'Status:'}</span>{" "}
                      <span className={`status-badge ${getStatusBadgeClass(task.status)}`}>
                        {displayStatus(task.status)}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

    </div>
  );
}

export default Requests;
